import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { UserService } from '../../core/services/user.service';
import { AuthService } from '../../core/services/auth.service';
import { ToastService } from '../../core/services/toast.service';
import { UserProfileResponse, UpdateUserRequest, ResetPasswordRequest } from '../../core/models/models';
import { PhoneInputComponent } from '../../shared/components/phone-input/phone-input.component';

@Component({
  selector: 'app-user-profile',
  standalone: true,
  imports: [CommonModule, FormsModule, PhoneInputComponent],
  template: `
    <div class="space-y-6 max-w-5xl mx-auto">
      <!-- Page Header -->
      <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-5">
        <div>
          <h1 class="text-2xl font-extrabold text-slate-900 tracking-tight flex items-center gap-2.5">
            <span class="w-2.5 h-6 bg-gradient-to-b from-emerald-600 to-teal-500 rounded-full inline-block"></span>
            User Profile & Security
          </h1>
          <p class="text-xs text-slate-500 mt-1 font-medium">
            Manage your personal contact details, institution affiliation, and multi-factor authentication security credentials
          </p>
        </div>
      </div>

      @if (loading()) {
        <div class="p-12 text-center text-slate-400">
          <div class="inline-block animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full mb-3"></div>
          <p class="text-xs font-semibold text-slate-500">Loading your profile details...</p>
        </div>
      } @else if (profile()) {
        <!-- Top Profile Summary Banner -->
        <div class="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm relative overflow-hidden">
          <div class="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-emerald-50 via-teal-50/40 to-transparent rounded-full -mr-20 -mt-20 pointer-events-none"></div>

          <div class="relative z-10 flex flex-col md:flex-row md:items-center gap-6">
            <!-- Avatar with Initials -->
            <div class="w-20 h-20 sm:w-24 sm:h-24 rounded-3xl bg-gradient-to-tr from-emerald-600 to-teal-500 text-white font-black text-2xl sm:text-3xl flex items-center justify-center shadow-lg shadow-emerald-600/20 ring-4 ring-white shrink-0">
              {{ getInitials() }}
            </div>

            <!-- Profile Info -->
            <div class="flex-1 space-y-2">
              <div class="flex flex-wrap items-center gap-2.5">
                <h2 class="text-xl sm:text-2xl font-black text-slate-900">
                  {{ profile()?.firstName }} {{ profile()?.middleName || '' }} {{ profile()?.lastName }}
                </h2>
                <span class="px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-emerald-100 text-emerald-800 border border-emerald-300">
                  {{ profile()?.status || 'ACTIVE' }}
                </span>
                <span class="px-2.5 py-0.5 rounded-full text-[11px] font-bold bg-slate-100 text-slate-700 border border-slate-300">
                  {{ profile()?.rank || 'OFFICER' }}
                </span>

                <!-- MFA Status Badge in Banner -->
                <span
                  class="px-2.5 py-0.5 rounded-full text-[11px] font-bold flex items-center gap-1.5 transition-colors"
                  [ngClass]="profile()?.mfaEnabled !== false ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'"
                  [title]="profile()?.mfaEnabled !== false ? 'Multi-Factor Authentication is active' : 'Multi-Factor Authentication is disabled'"
                >
                  <span class="w-2 h-2 rounded-full" [ngClass]="profile()?.mfaEnabled !== false ? 'bg-emerald-500' : 'bg-amber-500'"></span>
                  {{ profile()?.mfaEnabled !== false ? 'MFA ACTIVE' : 'MFA DISABLED' }}
                </span>
              </div>

              <div class="flex flex-wrap items-center gap-x-6 gap-y-1.5 text-xs text-slate-600">
                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  <span class="font-semibold">{{ profile()?.email }}</span>
                </div>

                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                  </svg>
                  <span>{{ profile()?.phone || 'No phone recorded' }}</span>
                </div>

                <div class="flex items-center gap-1.5">
                  <svg class="w-4 h-4 text-emerald-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                  </svg>
                  <span class="font-bold text-slate-800">{{ profile()?.institutionName || 'Zanzibar Government' }}</span>
                </div>
              </div>

              <!-- Roles Badges -->
              <div class="pt-2 flex flex-wrap gap-1.5">
                <span class="text-[11px] font-bold text-slate-400 self-center mr-1">Roles:</span>
                @for (role of profile()?.roles; track role) {
                  <span class="px-2.5 py-1 rounded-lg bg-emerald-50 text-emerald-900 border border-emerald-200 text-xs font-bold shadow-2xs">
                    {{ role }}
                  </span>
                }
              </div>
            </div>
          </div>
        </div>

        <!-- Navigation Tabs -->
        <div class="flex border-b border-slate-200 gap-4">
          <button
            (click)="activeTab.set('details')"
            class="pb-3 text-xs font-bold tracking-wider uppercase transition-colors relative cursor-pointer"
            [ngClass]="activeTab() === 'details' ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-slate-500 hover:text-slate-800'"
          >
            Personal & Institution Info
          </button>
          <button
            (click)="activeTab.set('password')"
            class="pb-3 text-xs font-bold tracking-wider uppercase transition-colors relative flex items-center gap-2 cursor-pointer"
            [ngClass]="activeTab() === 'password' ? 'text-emerald-700 border-b-2 border-emerald-600' : 'text-slate-500 hover:text-slate-800'"
          >
            <span>Security & Authentication</span>
            <span
              class="px-2 py-0.5 rounded-full text-[10px] font-bold"
              [ngClass]="profile()?.mfaEnabled !== false ? 'bg-emerald-100 text-emerald-800' : 'bg-amber-100 text-amber-800'"
            >
              {{ profile()?.mfaEnabled !== false ? 'MFA ON' : 'MFA OFF' }}
            </span>
          </button>
        </div>

        <!-- TAB 1: PERSONAL & INSTITUTION DETAILS -->
        @if (activeTab() === 'details') {
          <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <!-- Edit Profile Form -->
            <div class="lg:col-span-2 bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
              <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
                <svg class="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                Edit Contact Details
              </h3>

              <form (ngSubmit)="onUpdateProfile()" class="space-y-4">
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-bold text-slate-700 uppercase mb-1">First Name</label>
                    <input
                      type="text"
                      [(ngModel)]="editForm.firstName"
                      name="firstName"
                      required
                      class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Middle Name</label>
                    <input
                      type="text"
                      [(ngModel)]="editForm.middleName"
                      name="middleName"
                      class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>
                </div>

                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Last Name</label>
                    <input
                      type="text"
                      [(ngModel)]="editForm.lastName"
                      name="lastName"
                      required
                      class="w-full px-3.5 py-2.5 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                  </div>

                  <div>
                    <label class="block text-xs font-bold text-slate-700 uppercase mb-1">Official Phone Number</label>
                    <app-phone-input
                      [(ngModel)]="editForm.phone"
                      name="phone"
                      [required]="true"
                      placeholder="777 000 000"
                    ></app-phone-input>
                  </div>
                </div>

                <div class="pt-2 flex justify-end">
                  <button
                    type="submit"
                    [disabled]="savingProfile()"
                    class="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all disabled:opacity-50 cursor-pointer"
                  >
                    @if (savingProfile()) {
                      <span>Saving Changes...</span>
                    } @else {
                      <span>Save Profile Details</span>
                    }
                  </button>
                </div>
              </form>
            </div>

            <!-- Organization & Compliance Summary Card -->
            <div class="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-4">
              <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
                <svg class="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                Institutional Overview
              </h3>

              <div class="space-y-3 text-xs">
                <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div class="text-[10px] uppercase font-bold text-slate-400">Institution</div>
                  <div class="font-bold text-slate-900 mt-0.5">{{ profile()?.institutionName || 'N/A' }}</div>
                </div>

                <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div class="text-[10px] uppercase font-bold text-slate-400">Multi-Factor Authentication</div>
                  <div class="mt-1 flex items-center justify-between">
                    <div class="flex items-center gap-1.5 font-bold" [class.text-emerald-700]="profile()?.mfaEnabled !== false" [class.text-amber-700]="profile()?.mfaEnabled === false">
                      <span class="w-2 h-2 rounded-full" [class.bg-emerald-500]="profile()?.mfaEnabled !== false" [class.bg-amber-500]="profile()?.mfaEnabled === false"></span>
                      {{ profile()?.mfaEnabled !== false ? 'Enabled (Email OTP)' : 'Disabled' }}
                    </div>
                    <button
                      type="button"
                      (click)="activeTab.set('password')"
                      class="text-[11px] text-emerald-700 hover:text-emerald-800 font-semibold underline cursor-pointer"
                    >
                      Configure
                    </button>
                  </div>
                </div>

                <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div class="text-[10px] uppercase font-bold text-slate-400">Institutional SLA Status</div>
                  <div class="mt-1 flex items-center gap-1.5 font-bold" [class.text-emerald-700]="profile()?.slaAccepted" [class.text-amber-700]="!profile()?.slaAccepted">
                    <span class="w-2 h-2 rounded-full" [class.bg-emerald-500]="profile()?.slaAccepted" [class.bg-amber-500]="!profile()?.slaAccepted"></span>
                    {{ profile()?.slaAccepted ? 'SLA Agreed & Enforced' : 'Pending SLA Action' }}
                  </div>
                </div>

                <div class="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <div class="text-[10px] uppercase font-bold text-slate-400">Last System Access</div>
                  <div class="font-mono text-[11px] text-slate-700 mt-0.5">
                    {{ profile()?.lastLogin ? (profile()?.lastLogin | date:'medium') : 'Current Session' }}
                  </div>
                </div>
              </div>
            </div>
          </div>
        }

        <!-- TAB 2: SECURITY & AUTHENTICATION -->
        @if (activeTab() === 'password') {
          <div class="space-y-6 max-w-4xl">

            <!-- 1. MULTI-FACTOR AUTHENTICATION (MFA) CARD -->
            <div class="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
              <div class="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 border-b border-slate-200 pb-5">
                <div class="flex items-start gap-3.5">
                  <div
                    class="w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 shadow-md transition-colors"
                    [ngClass]="profile()?.mfaEnabled !== false ? 'bg-gradient-to-tr from-emerald-600 to-teal-500 text-white shadow-emerald-600/20' : 'bg-gradient-to-tr from-amber-500 to-orange-500 text-white shadow-amber-500/20'"
                  >
                    <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                    </svg>
                  </div>
                  <div>
                    <div class="flex items-center gap-2.5">
                      <h3 class="text-base font-bold text-slate-900">Multi-Factor Authentication (MFA / 2FA)</h3>
                      <span
                        class="px-2.5 py-0.5 rounded-full text-[10px] font-black uppercase tracking-wider flex items-center gap-1.5"
                        [ngClass]="profile()?.mfaEnabled !== false ? 'bg-emerald-100 text-emerald-800 border border-emerald-300' : 'bg-amber-100 text-amber-800 border border-amber-300'"
                      >
                        <span class="w-1.5 h-1.5 rounded-full" [ngClass]="profile()?.mfaEnabled !== false ? 'bg-emerald-500 animate-pulse' : 'bg-amber-500'"></span>
                        {{ profile()?.mfaEnabled !== false ? 'Protected / Active' : 'Disabled' }}
                      </span>
                    </div>
                    <p class="text-xs text-slate-500 mt-1">
                      Enforce second-factor email verification code challenges on portal sign-in
                    </p>
                  </div>
                </div>

                <!-- MFA Quick Action Button -->
                <div class="shrink-0 flex items-center">
                  @if (profile()?.mfaEnabled !== false) {
                    <button
                      type="button"
                      (click)="promptDisableMfa()"
                      [disabled]="updatingMfa()"
                      class="px-4 py-2.5 rounded-xl border border-rose-200 bg-rose-50/70 hover:bg-rose-100 text-rose-700 font-bold text-xs transition-all shadow-2xs flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      @if (updatingMfa()) {
                        <span class="w-3.5 h-3.5 border-2 border-rose-600 border-t-transparent rounded-full animate-spin"></span>
                        <span>Updating...</span>
                      } @else {
                        <svg class="w-4 h-4 text-rose-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                        </svg>
                        <span>Disable MFA</span>
                      }
                    </button>
                  } @else {
                    <button
                      type="button"
                      (click)="applyMfaUpdate(true)"
                      [disabled]="updatingMfa()"
                      class="px-5 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
                    >
                      @if (updatingMfa()) {
                        <span class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        <span>Enabling...</span>
                      } @else {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                        </svg>
                        <span>Enable MFA Protection</span>
                      }
                    </button>
                  }
                </div>
              </div>

              <!-- State Information Box -->
              @if (profile()?.mfaEnabled !== false) {
                <div class="p-5 rounded-2xl bg-emerald-50/70 border border-emerald-200 space-y-4">
                  <div class="flex items-start gap-3.5">
                    <div class="w-9 h-9 rounded-xl bg-emerald-500 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-emerald-500/20">
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2.5" d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                    <div class="text-xs text-slate-700 space-y-1">
                      <div class="font-bold text-slate-900 text-sm">Two-Factor Authentication is actively enforced</div>
                      <p class="leading-relaxed">
                        Whenever you sign in with your credentials, a secure 6-digit one-time passcode (OTP) challenge will be automatically dispatched to your verified government address:
                        <span class="font-bold text-emerald-900 bg-emerald-100 px-1.5 py-0.5 rounded ml-1">{{ profile()?.email }}</span>.
                      </p>
                    </div>
                  </div>

                  <div class="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-1 text-[11px]">
                    <div class="p-3 bg-white/90 rounded-xl border border-emerald-100 shadow-2xs">
                      <span class="text-slate-400 font-semibold block text-[10px] uppercase">Challenge Type</span>
                      <span class="font-bold text-slate-800">Email OTP Passcode</span>
                    </div>
                    <div class="p-3 bg-white/90 rounded-xl border border-emerald-100 shadow-2xs">
                      <span class="text-slate-400 font-semibold block text-[10px] uppercase">Passcode Validity</span>
                      <span class="font-bold text-slate-800">5 Minutes per attempt</span>
                    </div>
                    <div class="p-3 bg-white/90 rounded-xl border border-emerald-100 shadow-2xs">
                      <span class="text-slate-400 font-semibold block text-[10px] uppercase">Compliance Level</span>
                      <span class="font-bold text-emerald-700">ZAN-X Security Standard</span>
                    </div>
                  </div>
                </div>
              } @else {
                <div class="p-5 rounded-2xl bg-amber-50/70 border border-amber-200 space-y-3">
                  <div class="flex items-start gap-3.5">
                    <div class="w-9 h-9 rounded-xl bg-amber-500 text-white flex items-center justify-center shrink-0 mt-0.5 shadow-sm shadow-amber-500/20">
                      <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                    </div>
                    <div class="text-xs text-slate-700 space-y-1">
                      <div class="font-bold text-slate-900 text-sm">MFA Protection is currently turned off</div>
                      <p class="leading-relaxed">
                        Your account is currently protected by password alone. Without two-factor authentication, your session lacks the second verification layer required for high-assurance data sharing operations.
                      </p>
                    </div>
                  </div>

                  <div class="flex items-center gap-2 pt-1 text-[11px] text-amber-900">
                    <svg class="w-4 h-4 text-amber-600 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span>Click the <strong>"Enable MFA Protection"</strong> button above to instantly activate email OTP verification on your account.</span>
                  </div>
                </div>
              }
            </div>

            <!-- 2. CHANGE / RESET PASSWORD CARD -->
            <div class="bg-white rounded-3xl p-6 sm:p-8 border border-slate-200 shadow-sm space-y-6">
              <div class="border-b border-slate-200 pb-4">
                <h3 class="text-base font-bold text-slate-900 flex items-center gap-2">
                  <svg class="w-5 h-5 text-emerald-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                  Change / Reset Password
                </h3>
                <p class="text-xs text-slate-500 mt-1">
                  To reset your password, please confirm your current credentials followed by your new password.
                </p>
              </div>

              <form (ngSubmit)="onResetPassword()" class="space-y-4">
                <!-- Current Password -->
                <div>
                  <label for="current-pwd" class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Current Password
                  </label>
                  <div class="relative">
                    <input
                      id="current-pwd"
                      [type]="showCurrentPwd() ? 'text' : 'password'"
                      [(ngModel)]="pwdForm.currentPassword"
                      name="currentPassword"
                      required
                      placeholder="Enter your current password"
                      class="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                    <button
                      type="button"
                      (click)="showCurrentPwd.set(!showCurrentPwd())"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      @if (showCurrentPwd()) {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                      } @else {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      }
                    </button>
                  </div>
                </div>

                <!-- New Password -->
                <div>
                  <label for="profile-new-pwd" class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    New Password
                  </label>
                  <div class="relative">
                    <input
                      id="profile-new-pwd"
                      [type]="showNewPwd() ? 'text' : 'password'"
                      [(ngModel)]="pwdForm.newPassword"
                      name="newPassword"
                      required
                      minlength="8"
                      placeholder="Enter new password (min 8 characters)"
                      class="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                    />
                    <button
                      type="button"
                      (click)="showNewPwd.set(!showNewPwd())"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      @if (showNewPwd()) {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                      } @else {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      }
                    </button>
                  </div>
                </div>

                <!-- Confirm Password -->
                <div>
                  <label for="profile-confirm-pwd" class="block text-xs font-bold text-slate-700 uppercase mb-1">
                    Confirm New Password
                  </label>
                  <div class="relative">
                    <input
                      id="profile-confirm-pwd"
                      [type]="showConfirmPwd() ? 'text' : 'password'"
                      [(ngModel)]="confirmPassword"
                      name="confirmPassword"
                      required
                      placeholder="Confirm new password"
                      class="w-full px-3.5 py-2.5 pr-10 rounded-xl border border-slate-300 text-xs bg-white text-slate-900 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-500/20"
                      [class.border-rose-400]="confirmPassword && pwdForm.newPassword !== confirmPassword"
                      [class.border-emerald-400]="confirmPassword && pwdForm.newPassword === confirmPassword"
                    />
                    <button
                      type="button"
                      (click)="showConfirmPwd.set(!showConfirmPwd())"
                      class="absolute inset-y-0 right-0 pr-3 flex items-center text-slate-400 hover:text-slate-600 cursor-pointer"
                    >
                      @if (showConfirmPwd()) {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l18 18" /></svg>
                      } @else {
                        <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                      }
                    </button>
                  </div>
                  @if (confirmPassword && pwdForm.newPassword !== confirmPassword) {
                    <p class="text-[11px] text-rose-600 font-semibold mt-1">Passwords do not match</p>
                  }
                </div>

                <!-- Requirements box -->
                <div class="p-3 rounded-xl bg-slate-50 border border-slate-200 text-[11px] space-y-1 text-slate-600">
                  <div class="font-bold text-slate-700">Password Policy:</div>
                  <div class="flex items-center gap-1.5" [class.text-emerald-700]="pwdForm.newPassword.length >= 8">
                    <span>{{ pwdForm.newPassword.length >= 8 ? '✓' : '○' }}</span>
                    <span>Minimum 8 characters</span>
                  </div>
                  <div class="flex items-center gap-1.5" [class.text-emerald-700]="confirmPassword && pwdForm.newPassword === confirmPassword">
                    <span>{{ confirmPassword && pwdForm.newPassword === confirmPassword ? '✓' : '○' }}</span>
                    <span>Passwords match exactly</span>
                  </div>
                  <div class="flex items-center gap-1.5 text-slate-500">
                    <span>○</span>
                    <span>Cannot reuse your last 5 passwords</span>
                  </div>
                </div>

                <div class="pt-2 flex justify-end">
                  <button
                    type="submit"
                    [disabled]="updatingPwd() || !canUpdatePassword()"
                    class="px-6 py-2.5 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 text-white font-bold text-xs shadow-md shadow-emerald-600/20 transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                  >
                    @if (updatingPwd()) {
                      <span>Resetting Password...</span>
                    } @else {
                      <span>Update & Reset Password</span>
                    }
                  </button>
                </div>
              </form>
            </div>
          </div>
        }
      }

      <!-- CONFIRMATION MODAL FOR DISABLING MFA -->
      @if (showMfaDisableConfirmModal()) {
        <div class="fixed inset-0 bg-slate-950/60 backdrop-blur-xs z-50 flex items-center justify-center p-4">
          <div class="bg-white rounded-3xl max-w-md w-full p-6 sm:p-7 shadow-2xl border border-slate-200 space-y-4 animate-in fade-in zoom-in-95 duration-150">
            <div class="w-12 h-12 rounded-2xl bg-rose-100 text-rose-600 flex items-center justify-center mx-auto shadow-sm shadow-rose-600/10">
              <svg class="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>

            <div class="text-center space-y-1.5">
              <h4 class="text-base font-extrabold text-slate-900">Disable Multi-Factor Authentication?</h4>
              <p class="text-xs text-slate-500 leading-relaxed">
                Disabling MFA reduces the security of your government portal account. You will no longer be asked for an email verification code when signing in.
              </p>
            </div>

            <div class="p-3.5 rounded-xl bg-rose-50 border border-rose-200 text-[11px] text-rose-800 space-y-1">
              <div class="font-bold flex items-center gap-1.5">
                <svg class="w-3.5 h-3.5 text-rose-600 shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd"/>
                </svg>
                Security Advisory
              </div>
              <p class="leading-relaxed">Your account will be guarded by your password only. You can re-enable MFA at any time.</p>
            </div>

            <div class="flex items-center justify-end gap-2.5 pt-2">
              <button
                type="button"
                (click)="showMfaDisableConfirmModal.set(false)"
                class="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 transition-colors cursor-pointer"
              >
                Cancel, Keep Enabled
              </button>
              <button
                type="button"
                (click)="confirmDisableMfa()"
                [disabled]="updatingMfa()"
                class="px-5 py-2.5 rounded-xl text-xs font-bold bg-rose-600 hover:bg-rose-700 text-white shadow-md shadow-rose-600/20 transition-all flex items-center gap-2 cursor-pointer disabled:opacity-50"
              >
                @if (updatingMfa()) {
                  <span class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                  <span>Disabling...</span>
                } @else {
                  <span>Yes, Disable MFA</span>
                }
              </button>
            </div>
          </div>
        </div>
      }
    </div>
  `
})
export class UserProfileComponent implements OnInit {
  private userService = inject(UserService);
  private authService = inject(AuthService);
  private toastService = inject(ToastService);

  profile = signal<UserProfileResponse | null>(null);
  loading = signal<boolean>(true);
  activeTab = signal<'details' | 'password'>('details');

  // Edit details form
  editForm: UpdateUserRequest = {
    firstName: '',
    middleName: '',
    lastName: '',
    phone: '',
    status: 'ACTIVE'
  };
  savingProfile = signal<boolean>(false);

  // MFA state
  updatingMfa = signal<boolean>(false);
  showMfaDisableConfirmModal = signal<boolean>(false);

  // Password reset form
  pwdForm: ResetPasswordRequest = {
    currentPassword: '',
    newPassword: ''
  };
  confirmPassword = '';
  showCurrentPwd = signal<boolean>(false);
  showNewPwd = signal<boolean>(false);
  showConfirmPwd = signal<boolean>(false);
  updatingPwd = signal<boolean>(false);

  ngOnInit() {
    this.loadProfile();
  }

  loadProfile() {
    this.loading.set(true);
    this.userService.getCurrentUser().subscribe({
      next: (res) => {
        this.loading.set(false);
        if (res.data) {
          this.profile.set(res.data);
          this.editForm = {
            firstName: res.data.firstName,
            middleName: res.data.middleName || '',
            lastName: res.data.lastName,
            phone: res.data.phone || '',
            status: res.data.status || 'ACTIVE',
            mfaEnabled: res.data.mfaEnabled !== false
          };
        }
      },
      error: () => {
        this.loading.set(false);
        this.toastService.error('Could not load user profile.');
      }
    });
  }

  getInitials(): string {
    const p = this.profile();
    if (!p) return 'ZX';
    const first = p.firstName ? p.firstName[0] : '';
    const last = p.lastName ? p.lastName[0] : '';
    return (first + last).toUpperCase() || 'ZX';
  }

  promptDisableMfa() {
    this.showMfaDisableConfirmModal.set(true);
  }

  confirmDisableMfa() {
    this.showMfaDisableConfirmModal.set(false);
    this.applyMfaUpdate(false);
  }

  applyMfaUpdate(enable: boolean) {
    this.updatingMfa.set(true);
    this.userService.updateCurrentUserMfa(enable).subscribe({
      next: (res) => {
        this.updatingMfa.set(false);
        if (res.data) {
          this.profile.set(res.data);
        } else if (this.profile()) {
          this.profile.set({ ...this.profile()!, mfaEnabled: enable });
        }
        this.toastService.success(
          res.message || (enable ? 'Multi-Factor Authentication enabled successfully!' : 'Multi-Factor Authentication disabled.')
        );
      },
      error: (err) => {
        this.updatingMfa.set(false);
        this.toastService.error(err.error?.message || 'Could not update MFA status. Please try again.');
      }
    });
  }

  onUpdateProfile() {
    const p = this.profile();
    if (!p || !p.uid) return;

    this.savingProfile.set(true);
    this.userService.updateUser(p.uid, this.editForm).subscribe({
      next: (res) => {
        this.savingProfile.set(false);
        this.toastService.success('Profile details updated successfully!');
        if (res.data) {
          this.profile.set(res.data);
          // Update cached auth state
          const cur = this.authService.currentUser();
          if (cur) {
            const updated = {
              ...cur,
              fullName: `${res.data.firstName} ${res.data.lastName}`.trim()
            };
            this.authService.currentUser.set(updated);
            localStorage.setItem('zanx_auth_user', JSON.stringify(updated));
          }
        }
      },
      error: (err) => {
        this.savingProfile.set(false);
        this.toastService.error(err.error?.message || 'Could not update profile.');
      }
    });
  }

  canUpdatePassword(): boolean {
    return !!(
      this.pwdForm.currentPassword &&
      this.pwdForm.newPassword.length >= 8 &&
      this.pwdForm.newPassword === this.confirmPassword
    );
  }

  onResetPassword() {
    if (!this.canUpdatePassword()) return;

    this.updatingPwd.set(true);
    this.userService.changeCurrentUserPassword(this.pwdForm).subscribe({
      next: () => {
        this.updatingPwd.set(false);
        this.toastService.success('Password updated successfully!');
        this.pwdForm = { currentPassword: '', newPassword: '' };
        this.confirmPassword = '';
      },
      error: (err) => {
        this.updatingPwd.set(false);
        this.toastService.error(err.error?.message || 'Failed to update password. Verify your current password.');
      }
    });
  }
}
